import { isCompanyCountryCode } from '@/lib/countries';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { buildBackendFilters, normalizeFilterParams, parseMetricURLBounds, sanitizeFinancialYear } from '@/lib/search-filters';
import type { FilterParams } from '@/lib/search-filter-params';
import { filterParamsToParsed } from '@/lib/seo-lists/filter-adapter';
import type { CompanySearchResponse } from '@/lib/actions/company-search';
import type { NaceCode } from './searchPageTypes';

export function filtersToSearchParams(
  filters: FilterParams,
  naceCodes: NaceCode[],
  overrides: {
    sort: string;
    offset: number;
    limit: number;
  },
) {
  return buildBackendFilters({ ...filterParamsToParsed(filters), ...overrides }, { naceCodes });
}

/**
 * The filters behind the rendered search: URL params win, then the preset a
 * /listen/[slug] page supplies, then the empty default.
 */
export function buildInitialFilters(
  searchParams: URLSearchParams,
  presetFilters: Partial<FilterParams> | undefined,
  listSlug: string | undefined,
  pathname: string,
  locale: Locale,
): FilterParams {
  const preset = normalizeFilterParams(listSlug && pathname === localizePathname('/search', locale as Locale) ? undefined : presetFilters);
  const urlBounds = parseMetricURLBounds(Object.fromEntries([...new Set(searchParams.keys())].map((key) => [key, searchParams.getAll(key)])));
  // URL param wins when present; otherwise fall back to the preset supplied
  // by /listen/[slug] landing pages; otherwise empty default.
  const pickStr = (key: string, presetVal?: string): string =>
    searchParams.get(key) || presetVal || '';
  const pickArr = (key: string, presetVal?: string[]): string[] => {
    const fromUrl = searchParams.getAll(key);
    return fromUrl.length > 0 ? fromUrl : (presetVal ?? []);
  };
  const pickBool = (key: string, presetVal?: boolean): boolean =>
    searchParams.has(key)
      ? searchParams.get(key) === 'true'
      : (presetVal ?? false);

  // Tri-state sibling of pickBool: only the literal "true"/"false" bind,
  // so an absent key stays `undefined` (no filter) rather than collapsing
  // into `false`, which here is a real selection.
  const pickTri = (
    key: string,
    presetVal: boolean | undefined,
  ): boolean | undefined => {
    const raw = searchParams.get(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return presetVal;
  };

  return {
    q: pickStr('q', preset.q),
    city: pickArr('city', preset.city),
    registerId: pickStr('registerId', preset.registerId),
    registerCourt: pickStr('registerCourt', preset.registerCourt),
    registerType: pickStr('registerType', preset.registerType),
    euid: pickStr('euid', preset.euid),
    Bundesland: pickArr('Bundesland', preset.Bundesland),
    revenueMin: searchParams.has('revenueMin') || searchParams.has('revenueMax') || searchParams.has('revenueRange') ? urlBounds.revenueMin : preset.revenueMin,
    revenueMax: searchParams.has('revenueMin') || searchParams.has('revenueMax') || searchParams.has('revenueRange') ? urlBounds.revenueMax : preset.revenueMax,
    revenueIncludeNull: pickBool(
      'revenueIncludeNull',
      preset.revenueIncludeNull,
    ),
    aktivaMin: searchParams.has('aktivaMin') || searchParams.has('aktivaMax') || searchParams.has('aktivaRange') ? urlBounds.aktivaMin : preset.aktivaMin,
    aktivaMax: searchParams.has('aktivaMin') || searchParams.has('aktivaMax') || searchParams.has('aktivaRange') ? urlBounds.aktivaMax : preset.aktivaMax,
    financialYear:
      sanitizeFinancialYear(searchParams.get('year')) ??
      sanitizeFinancialYear(preset.financialYear),
    profitMin: searchParams.has('profitMin') || searchParams.has('profitMax') || searchParams.has('profitRange') ? urlBounds.profitMin : preset.profitMin,
    profitMax: searchParams.has('profitMin') || searchParams.has('profitMax') || searchParams.has('profitRange') ? urlBounds.profitMax : preset.profitMax,
    employeesMin: searchParams.has('employeesMin') || searchParams.has('employeesMax') || searchParams.has('employeeRange') ? urlBounds.employeesMin : preset.employeesMin,
    employeesMax: searchParams.has('employeesMin') || searchParams.has('employeesMax') || searchParams.has('employeeRange') ? urlBounds.employeesMax : preset.employeesMax,
    ageRanges: pickArr('ageRange', preset.ageRanges),
    naceCodes: pickArr('naceCode', preset.naceCodes),
    industries: pickArr('industry', preset.industries),
    foundedAtFrom: pickStr('foundedAtFrom', preset.foundedAtFrom),
    foundedAtTo: pickStr('foundedAtTo', preset.foundedAtTo),
    tags: pickArr('tag', preset.tags),
    personName: pickStr('person_name', preset.personName),
    rechtsformCodes: pickArr(
      'rechtsformCode',
      preset.rechtsformCodes,
    ),
    cpvCode: pickStr('cpvCode', preset.cpvCode),
    hasYearlyMetadata: pickBool(
      'hasYearlyMetadata',
      preset.hasYearlyMetadata,
    ),
    memberships: pickArr('membership', preset.memberships),
    legalStatus: pickArr('legalStatus', preset.legalStatus),
    companySize: pickArr('companySize', preset.companySize),
    // Every country is the default; an explicit DE / CH (old links, saved
    // searches) still narrows. 'all' is the legacy spelling of the default.
    country: (() => {
      if (!searchParams.has('country')) return preset.country;
      const c = (searchParams.get('country') || '').toUpperCase();
      return isCompanyCountryCode(c) ? c : '';
    })(),
    tenderRole: pickArr('tenderRole', preset.tenderRole),
    familyManaged: pickTri('familyManaged', preset.familyManaged),
    founderLed: pickTri('founderLed', preset.founderLed),
    successionRisk: pickTri('successionRisk', preset.successionRisk),
    leiCode: pickStr('leiCode', preset.leiCode),
    // State holds the digit tail only — the UI shows a fixed "DE" prefix
    // and the backend mapping prepends DE when sending. Strip any leading
    // DE from an external URL so we don't double-prefix.
    ustIdnr: (searchParams.get('ustIdnr') || preset.ustIdnr || '')
      .replace(/^DE/i, '')
      .replace(/\D/g, ''),
    hasWebsite: pickBool('hasWebsite', preset.hasWebsite),
    hasEmail: pickBool('hasEmail', preset.hasEmail),
  };
}

/** How many filter values a submitted search carries (search_performed event). */
export function submittedFilterCount(filters: FilterParams): number {
  const filterCount =
    filters.Bundesland.length +
    (filters.revenueMin !== undefined || filters.revenueMax !== undefined ? 1 : 0) +
    (filters.aktivaMin !== undefined || filters.aktivaMax !== undefined ? 1 : 0) +
    (filters.profitMin !== undefined || filters.profitMax !== undefined ? 1 : 0) +
    (filters.employeesMin !== undefined || filters.employeesMax !== undefined ? 1 : 0) +
    filters.ageRanges.length +
    filters.naceCodes.length +
    filters.industries.length +
    filters.tags.length +
    (filters.personName ? 1 : 0) +
    filters.city.length +
    (filters.registerId ? 1 : 0) +
    (filters.registerCourt ? 1 : 0) +
    (filters.registerType ? 1 : 0) +
    (filters.euid ? 1 : 0) +
    (filters.hasYearlyMetadata ? 1 : 0) +
    (filters.foundedAtFrom ? 1 : 0) +
    (filters.foundedAtTo ? 1 : 0) +
    filters.rechtsformCodes.length +
    (filters.cpvCode ? 1 : 0) +
    filters.memberships.length +
    filters.legalStatus.length +
    filters.tenderRole.length +
    (filters.familyManaged !== undefined ? 1 : 0) +
    (filters.founderLed !== undefined ? 1 : 0) +
    (filters.successionRisk !== undefined ? 1 : 0) +
    (filters.leiCode ? 1 : 0) +
    (filters.ustIdnr ? 1 : 0) +
    (filters.hasWebsite ? 1 : 0) +
    (filters.hasEmail ? 1 : 0);
  return filterCount;
}

/** The /api/log/export payload: the export stage plus the filters behind the results. */
export function exportLogBody(
  stage: 'click' | 'completed',
  extra: Record<string, unknown> | undefined,
  url: string,
  isProOrAdmin: boolean,
  searchParams: URLSearchParams,
  initialFilters: FilterParams,
  currentSort: string,
  searchResponse: Pick<CompanySearchResponse, 'total'>,
) {
  const body = {
    stage,
    canExport: isProOrAdmin,
    keyword: searchParams.get('q') || undefined,
    city: searchParams.getAll('city').join(',') || undefined,
    bundesland: searchParams.getAll('Bundesland').join(',') || undefined,
    revenueMin: initialFilters.revenueMin,
    revenueMax: initialFilters.revenueMax,
    aktivaMin: initialFilters.aktivaMin,
    aktivaMax: initialFilters.aktivaMax,
    profitMin: initialFilters.profitMin,
    profitMax: initialFilters.profitMax,
    employeesMin: initialFilters.employeesMin,
    employeesMax: initialFilters.employeesMax,
    naceCodes: searchParams.getAll('naceCode').join(',') || undefined,
    industries: searchParams.getAll('industry').join(',') || undefined,
    tags: searchParams.getAll('tag').join(',') || undefined,
    personName: searchParams.get('person_name') || undefined,
    foundedAtFrom: searchParams.get('foundedAtFrom') || undefined,
    foundedAtTo: searchParams.get('foundedAtTo') || undefined,
    sort: currentSort,
    resultCount: searchResponse.total,
    url,
    ...extra,
  };
  return body;
}
